import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type Comment,
  type CommentListQuery,
  commentText,
  type CommentThread as Thread,
  toThreads,
} from "~/api/commentTypes";
import {
  useComments,
  useCreateComment,
  useDeleteComment,
  useDocumentConversations,
  useResolveComment,
  useUpdateComment,
  useViewer,
} from "~/api/queries";
import { ABILITY, type Document } from "~/api/types";
import { ThinkThread } from "~/features/comments/ThinkThread";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { cn } from "~/foundation/utils/cn";
import { formatDateTime } from "~/foundation/utils/time";

/** How many replies show before the thread collapses the middle. */
const VISIBLE_REPLIES = 3;

type StatusFilter = "unresolved" | "resolved" | "all";

/**
 * Comments on a document (W8 §B.9 / S10).
 *
 * Built clean-room from `CommentsController`: Outline's thread UI was not read and is not
 * reproduced. What IS ported is the owner's delta — the think thread (see `ThinkThread`).
 *
 * Two server facts shape every control here:
 *
 *  - **`comments.*` is the wave's only `{data, policies}` family, and its policies say
 *    nothing** — `BuildPolicies` sets all 34 ability keys `true` for every id. So the gates
 *    are the document's `documentComment` plus own-authorship, applied client-side, and the
 *    fixture enforces exactly those (the server checks NEITHER on update or delete).
 *  - **`resolve` throws for a reply.** Resolution belongs to the thread root, so it is offered
 *    there and nowhere else.
 *
 * Reaction counts are deliberately absent rather than shown as zero: the server's cached
 * `reactions` column is never refreshed (W8-5), so a count rendered from a comment payload
 * would always read "nobody reacted". `api.comments.reactions()` folds the real rows when a
 * surface needs them.
 */
export function CommentsPanel({ document, ports }: { document: Document; ports: DocumentPorts }) {
  const { t, i18n } = useTranslation();
  const { data: viewer } = useViewer();
  const [status, setStatus] = useState<StatusFilter>("unresolved");
  const [sort, setSort] = useState<"createdAt" | "updatedAt">("createdAt");
  const [draft, setDraft] = useState("");
  const [deleting, setDeleting] = useState<Comment | null>(null);

  const canComment = document.abilities[ABILITY.documentComment] === true;

  const query: CommentListQuery = useMemo(
    () => ({
      documentId: document.id,
      sort,
      direction: "ASC",
      ...(status === "all" ? {} : { statusFilter: [status] as Array<"resolved" | "unresolved"> }),
    }),
    [document.id, sort, status],
  );

  const page = useComments(query);
  const create = useCreateComment();
  const resolve = useResolveComment();
  const remove = useDeleteComment();

  /**
   * Which threads are ALSO conversations. There is no flag for this on either side — a thread
   * is a think thread exactly when a conversation shares its id, so the document's
   * conversation list is the lookup.
   */
  const conversations = useDocumentConversations(document.id, { limit: 50 });
  const thinkIds = useMemo(
    () => new Set((conversations.data ?? []).map((entry) => entry.id)),
    [conversations.data],
  );

  const threads = useMemo(() => toThreads(page.data?.comments ?? []), [page.data]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-1">
        <div className="flex gap-0.5" role="group" aria-label={t("Show")}>
          {(["unresolved", "resolved", "all"] as StatusFilter[]).map((entry) => (
            <button
              key={entry}
              type="button"
              aria-pressed={status === entry}
              onClick={() => setStatus(entry)}
              className={cn(
                "rounded-sm px-2 py-0.5 text-caption",
                status === entry
                  ? "bg-accent text-accent-text"
                  : "bg-bg-secondary text-text-secondary hover:bg-hover-bg",
              )}
            >
              {entry === "unresolved" ? t("Open") : entry === "resolved" ? t("Resolved") : t("All")}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSort(sort === "createdAt" ? "updatedAt" : "createdAt")}
          className="ms-auto text-caption text-link hover:underline"
        >
          {sort === "createdAt" ? t("Oldest first") : t("Recently active")}
        </button>
      </div>

      <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {page.isLoading ? <Spinner label={t("Loading")} /> : null}
        {!page.isLoading && threads.length === 0 ? (
          <Empty message={t("No comments here yet.")} />
        ) : null}

        {threads.map((thread) => (
          <ThreadCard
            key={thread.id}
            thread={thread}
            document={document}
            ports={ports}
            canComment={canComment}
            viewerId={viewer?.id ?? ""}
            isThinkThread={thinkIds.has(thread.id)}
            language={i18n.language}
            onResolve={(resolved) => resolve.mutate({ id: thread.id, resolved })}
            onReply={(text) =>
              create.mutate({ documentId: document.id, text, parentCommentId: thread.id })
            }
            onDelete={setDeleting}
          />
        ))}
      </div>

      <div className="flex flex-col gap-1 border-t border-divider pt-2">
        <textarea
          value={draft}
          aria-label={t("New comment")}
          placeholder={t("Add a comment…")}
          disabled={!canComment}
          onChange={(event) => setDraft(event.target.value)}
          className="min-h-16 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text disabled:opacity-50"
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="primary"
            disabled={!canComment || draft.trim().length === 0 || create.isPending}
            onClick={() =>
              create.mutate(
                { documentId: document.id, text: draft.trim() },
                { onSuccess: () => setDraft("") },
              )
            }
          >
            {t("Comment")}
          </Button>
          {!canComment ? (
            <Text variant="small" className="text-text-tertiary">
              {t("You can read these comments, but not add to them.")}
            </Text>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleting(null);
          }
        }}
        title={t("Delete this comment?")}
        description={t("It disappears for everyone. Replies to it stay.")}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={remove.isPending}
        onConfirm={() => {
          if (deleting) {
            remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
          }
        }}
      />
    </div>
  );
}

function ThreadCard({
  thread,
  document,
  ports,
  canComment,
  viewerId,
  isThinkThread,
  language,
  onResolve,
  onReply,
  onDelete,
}: {
  thread: Thread;
  document: Document;
  ports: DocumentPorts;
  canComment: boolean;
  viewerId: string;
  isThinkThread: boolean;
  language: string;
  onResolve: (resolved: boolean) => void;
  onReply: (text: string) => void;
  onDelete: (comment: Comment) => void;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState("");
  const [replying, setReplying] = useState(false);

  const hidden = Math.max(0, thread.replies.length - VISIBLE_REPLIES);
  const shown = expanded ? thread.replies : thread.replies.slice(-VISIBLE_REPLIES);
  const anchorText = thread.root.anchorText ?? commentText(thread.root.data);

  return (
    <section
      className={cn(
        "flex flex-col gap-1.5 rounded-md border p-2",
        thread.resolved ? "border-divider opacity-70" : "border-divider",
      )}
    >
      {thread.root.anchorText ? (
        <blockquote className="border-accent border-s-2 ps-2 text-start text-caption text-text-tertiary">
          {thread.root.anchorText}
        </blockquote>
      ) : null}

      <CommentRow
        comment={thread.root}
        viewerId={viewerId}
        language={language}
        onDelete={onDelete}
      />

      {hidden > 0 && !expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="self-start text-caption text-link hover:underline"
        >
          {t("Show {{count}} more replies", { count: hidden })}
        </button>
      ) : null}

      {shown.map((comment) => (
        <CommentRow
          key={comment.id}
          comment={comment}
          viewerId={viewerId}
          language={language}
          onDelete={onDelete}
          indented
        />
      ))}

      {/* The owner's delta: this thread is also a conversation, addressed to thinkers. */}
      {isThinkThread ? (
        <ThinkThread
          threadId={thread.id}
          document={document}
          anchorText={anchorText}
          ports={ports}
          canComment={canComment}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-1">
        {replying ? (
          <div className="flex w-full flex-col gap-1">
            <textarea
              value={reply}
              aria-label={t("Reply")}
              onChange={(event) => setReply(event.target.value)}
              className="min-h-14 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text"
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="primary"
                disabled={reply.trim().length === 0}
                onClick={() => {
                  onReply(reply.trim());
                  setReply("");
                  setReplying(false);
                }}
              >
                {t("Reply")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReplying(false)}>
                {t("Cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={!canComment}
              onClick={() => setReplying(true)}
            >
              {t("Reply")}
            </Button>
            {/* Only a ROOT can be resolved — `Comment.resolve` throws for a reply. */}
            <Button
              size="sm"
              variant="ghost"
              disabled={!canComment}
              onClick={() => onResolve(!thread.resolved)}
            >
              {thread.resolved ? t("Reopen") : t("Resolve")}
            </Button>
            {thread.resolved && thread.root.resolvedBy ? (
              <Text variant="small" className="text-text-tertiary">
                {t("Resolved by {{name}}", { name: thread.root.resolvedBy.name })}
              </Text>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

function CommentRow({
  comment,
  viewerId,
  language,
  onDelete,
  indented,
}: {
  comment: Comment;
  viewerId: string;
  language: string;
  onDelete: (comment: Comment) => void;
  indented?: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateComment();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => commentText(comment.data));

  // The server has no ownership check on update or delete. This is the only gate there is.
  const mine = comment.createdById === viewerId;

  return (
    <article className={cn("flex flex-col gap-0.5", indented ? "ps-4" : null)}>
      <header className="flex items-center gap-2">
        <Text variant="small" className="font-medium text-text">
          {comment.createdBy?.name ?? t("Someone")}
        </Text>
        {comment.createdAt ? (
          <span className="text-caption text-text-tertiary">
            {formatDateTime(comment.createdAt, language)}
          </span>
        ) : null}
        {mine && !editing ? (
          <div className="ms-auto flex gap-0.5">
            <button
              type="button"
              onClick={() => {
                setDraft(commentText(comment.data));
                setEditing(true);
              }}
              className="text-caption text-text-tertiary hover:text-text"
            >
              {t("Edit")}
            </button>
            <button
              type="button"
              onClick={() => onDelete(comment)}
              className="text-caption text-text-tertiary hover:text-danger"
            >
              {t("Delete")}
            </button>
          </div>
        ) : null}
      </header>

      {editing ? (
        <div className="flex flex-col gap-1">
          <textarea
            value={draft}
            aria-label={t("Edit comment")}
            onChange={(event) => setDraft(event.target.value)}
            className="min-h-14 w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text"
          />
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                update.mutate(
                  { id: comment.id, text: draft.trim() },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              {t("Save")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-start text-body text-text-secondary">
          {commentText(comment.data)}
        </p>
      )}
    </article>
  );
}
